import { CheckboxField, Flex } from '@bridebook/aura';
import { interactedWithGuestCommsAction } from '@bridebook/web/lib/inbox/guests/analytics/actions';
import { useConfirmExitFlowModal } from '@bridebook/web/lib/inbox/guests/create-message-flow/use-confirm-exit-flow-modal';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch } from '@bridebook/web/lib/utils';
import { useWeddingWebsite } from '@bridebook/web/lib/wedding-website/hooks/query';
import { getWeddingWebsiteFlags } from '@bridebook/web/lib/wedding-website/hooks/query/selectors';
import { Trans, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Label } from '../components/form-components';
import { MessageFormSchema } from './message-form';

export const WeddingWebsiteSection = () => {
  const { t } = useTranslation('inbox');

  const { control, watch, setValue } = useFormContext<MessageFormSchema>();

  const { data: weddingWebsiteFlags } = useWeddingWebsite({
    select: getWeddingWebsiteFlags,
  });

  const { isCreated, isPublished, hasPassword } = weddingWebsiteFlags ?? {};

  const shareWeddingWebsiteLink = watch('shareWeddingWebsiteLink');
  useEffect(() => {
    setValue('displayPasswordInEmail', shareWeddingWebsiteLink);
  }, [setValue, shareWeddingWebsiteLink]);

  return (
    <>
      {/* SHARE LINK */}
      <Flex className="flex-col gap-2 py-2">
        <Label htmlFor="shareWeddingWebsiteLink">
          {t('guests.createMessageFlow.messageForm.shareLink.label')}
        </Label>
        <div className="">
          <div className="flex h-[54px] items-center">
            <Controller
              control={control}
              name="shareWeddingWebsiteLink"
              render={({ field: { value, onChange } }) => (
                <CheckboxField
                  disabled={!isCreated}
                  id="shareWeddingWebsiteLink"
                  label={t('guests.createMessageFlow.messageForm.shareLink.checkboxLabel')}
                  checked={value}
                  onCheckedChange={onChange}
                />
              )}
            />
          </div>
          {/* HINT HERE */}
          {!isCreated && <WeddingWebsiteNotCreatedHint />}
          {isCreated && !isPublished && <WeddingWebsiteNotPublishedHint />}
        </div>
      </Flex>
      {/* WEBSITE PASSWORD - show only if the website is created and has a password */}
      {isCreated && hasPassword && shareWeddingWebsiteLink && (
        <Flex className="flex-col gap-2 py-2">
          <Label htmlFor="displayPasswordInEmail">
            {t('guests.createMessageFlow.messageForm.websitePassword.label')}
          </Label>
          <div className="flex h-[54px] items-center">
            <Controller
              control={control}
              name="displayPasswordInEmail"
              render={({ field: { value, onChange } }) => (
                <CheckboxField
                  id="displayPasswordInEmail"
                  label={t('guests.createMessageFlow.messageForm.websitePassword.checkboxLabel')}
                  checked={value}
                  onCheckedChange={onChange}
                />
              )}
            />
          </div>
        </Flex>
      )}
    </>
  );
};

const Hint = ({ children }: { children: React.ReactNode }) => (
  <div className="text-xs font-semibold text-space-60">{children}</div>
);

const WeddingWebsiteNotCreatedHint = () => {
  const { push } = useRouter();
  const closeWithConfirmation = useConfirmExitFlowModal();
  const dispatch = useDispatch();

  const handleClick = () => {
    dispatch(
      interactedWithGuestCommsAction({
        action: 'clicked',
        location: 'guestComms',
        section: 'writeMessage',
        element: 'weddingWebsiteHyperlink',
        elementLabel: 'Create one',
      }),
    );
    closeWithConfirmation(() => {
      push(UrlHelper.website.main);
      dispatch(
        interactedWithGuestCommsAction({
          action: 'clicked',
          location: 'guestComms',
          section: 'writeMessage',
          element: 'weddingWebsiteHyperlink',
          elementLabel: 'Leave anyway',
        }),
      );
    });
  };

  return (
    <Hint>
      <Trans
        ns="inbox"
        i18nKey="guests.createMessageFlow.messageForm.shareLink.weddingWebsiteNotCreatedHint">
        You haven’t set up your wedding website yet.{' '}
        <button
          onClick={handleClick}
          className="text-left text-indigo-crush hover:underline"
          type="button">
          Create one first
        </button>{' '}
        to be able to attach it here.
      </Trans>
    </Hint>
  );
};

const WeddingWebsiteNotPublishedHint = () => {
  const { t } = useTranslation('inbox');
  return (
    <Hint>
      {t('guests.createMessageFlow.messageForm.shareLink.weddingWebsiteNotPublishedHint')}
    </Hint>
  );
};
